/* eslint-disable @typescript-eslint/no-unsafe-argument */
import type {
  TAvailableStatusesData,
  TGetAvailableStatusesResponse,
} from "@/app/(authenticated)/dashboard/_modules/types/types";
import type {
  GetLeadsParams,
  GetLeadsResponse,
} from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import { constructUrl, getParams } from "@/app/_module/services/helper";
import { showToast } from "@/app/_module/utils/show-toast";
import { isRateLimitError, getRateLimitMessage } from "@/app/_module/utils/rate-limit";
import { transformErrorResponse } from "@/app/_module/utils/transform-error-response";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";

import type {
  CreateLeadResponse,
  CreateLeadsParams,
} from "../types/create-lead-type";
import type {
  CreatePolicyParams,
  CreatePolicyResponse,
} from "../types/create-policy-type";
import type {
  CreateLeadNoteRequest,
  CreateLeadNoteResponse,
} from "../types/create-lead-note";
import type {
  DeleteLeadNoteRequest,
  DeleteLeadNoteResponse,
} from "../types/delete-lead-note";
import type { DeleteLeadResponse } from "../types/delete-lead-type";
import type { GetDeletedLeadsResponse } from "../types/get-deleted-leads-type";
import type { GetLeadByIdResponse } from "../types/get-leads-by-id-type";
import type { GetLeadsStatResponse } from "../types/search-lead-types";
import type {
  TActivateLeadsImportParams,
  TActivateLeadsImportResponseData,
  TActivateSingleImportResponseData,
  TFileUploadResponse,
  TGetLeadsImportbyIdParams,
  TGetLeadsImportbyIdResponse,
} from "../types/lead-import-types";
import type { LeadWithPoliciesResponse } from "../types/lead-with-policies-type";
import type {
  TLeadsImportListResponse,
  TImportDeletionPreview,
  TImportDeletionResult,
  TImportMoveResult,
} from "../types/leads-import-list-type";
import type { RestoreDeletedLeadsResponse } from "../types/restore-deleted-leads-type";
import {
  CreateBeneficiariesRequest,
  CreateBeneficiariesResponse,
  GetBeneficiariesResponse,
  UpdateBeneficiaryRequest,
  UpdateBeneficiaryResponse,
  DeleteBeneficiaryResponse,
} from "../types/Beneficiary";

export const leadsService = {
  // Get all leads with pagination and optional search
  getLeads: async (param: GetLeadsParams) => {
    let params;
    if (param) {
      params = getParams<GetLeadsParams>(param);
    }

    const url = params
      ? constructUrl(getApiUrl(endpoint.leads.getLeads), params)
      : getApiUrl(endpoint.leads.getLeads);

    try {
      const result = await handleApiCall<GetLeadsResponse>(async () => {
        const response = await api.get<GetLeadsResponse>({
          url,
          auth: true, // Add authentication
        });
        return response;
      });
      // handleApiCall returns { statusCode, message, data, timestamp }
      return (result as any).data || result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Get lead statistics
  getLeadStats: async (range?: { startDate?: string; endDate?: string }) => {
    try {
      const result = await handleApiCall<GetLeadsStatResponse>(async () => {
        // Optional window. Callers that pass nothing keep the previous ALL-TIME
        // behaviour, so the Leads page and admin panel are unaffected.
        const qs = new URLSearchParams();
        if (range?.startDate) qs.set('startDate', range.startDate);
        if (range?.endDate) qs.set('endDate', range.endDate);
        const suffix = qs.toString() ? `?${qs.toString()}` : '';
        const response = await api.get<GetLeadsStatResponse>({
          url: `${getApiUrl(endpoint.leads.getLeadsStats)}${suffix}`,
          auth: true, // Add authentication
        });
        return response;
      });
      // Handle both wrapped and direct response formats
      if (result.data) {
        return result.data;
      } else if ((result as any).leadsByStatus) {
        return result as any;
      } else {
        return null;
      }
    } catch (error) {
      // Log errors but don't spam toasts during backend issues

      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Search leads
  searchLeads: async (param: GetLeadsParams) => {
    let params;
    if (param) {
      params = getParams<GetLeadsParams>(param);
    }

    const url = params
      ? constructUrl(getApiUrl(endpoint.leads.searchLeads), params)
      : getApiUrl(endpoint.leads.searchLeads);

    try {
      const result = await handleApiCall<GetLeadsResponse>(async () => {
        const response = await api.get<GetLeadsResponse>({
          url,
          auth: true, // Add authentication
        });
        return response;
      });
      // handleApiCall returns { statusCode, message, data, timestamp }
      return (result as any).data || result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Get single lead
  getLead: async (leadId: string) => {
    try {
      return await handleApiCall<GetLeadByIdResponse>(async () => {
        const response = await api.get<GetLeadByIdResponse>({
          url: getApiUrl(endpoint.leads.getLeadById(leadId)),
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Create new lead
  createLead: async (leadData: CreateLeadsParams) => {
    try {
      return await handleApiCall<CreateLeadResponse>(async () => {
        const response = await api.post<CreateLeadResponse>({
          url: getApiUrl(endpoint.leads.createLead),
          body: leadData,
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Delete lead - Soft delete
  // When deleteHouseholdMembers is true, also deletes all household members
  deleteLead: async (leadId: string, deleteHouseholdMembers: boolean = false) => {
    try {
      const url = deleteHouseholdMembers
        ? `${getApiUrl(endpoint.leads.deleteLead(leadId))}?deleteHouseholdMembers=true`
        : getApiUrl(endpoint.leads.deleteLead(leadId));

      return await handleApiCall<DeleteLeadResponse>(async () => {
        const response = await api.delete<DeleteLeadResponse>({
          url,
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Bulk delete leads - Much faster than individual deletes
  // Sends all lead IDs in a single request to the backend
  bulkDeleteLeads: async (leadIds: string[], deleteHouseholdMembers: boolean = false) => {
    try {
      return await handleApiCall<{
        deletedCount: number;
        leadsDeleted: number;
        householdMembersDeleted: number;
        requestedCount: number;
      }>(async () => {
        const response = await api.post<{
          deletedCount: number;
          leadsDeleted: number;
          householdMembersDeleted: number;
          requestedCount: number;
        }>({
          url: getApiUrl(endpoint.leads.bulkDeleteLeads),
          body: { leadIds, deleteHouseholdMembers },
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Restore deleted lead
  restoreDeletedLead: async (leadId: string) => {
    try {
      const result = await handleApiCall<RestoreDeletedLeadsResponse>(
        async () => {
          const response = await api.post<RestoreDeletedLeadsResponse>({
            url: getApiUrl(endpoint.leads.restoreDeletedLead(leadId)),
            auth: true, // Add authentication
          });
          return response;
        }
      );

      showToast("Lead restored successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Permanently delete lead
  permanentlyDeleteLead: async (leadId: string) => {
    try {
      const result = await handleApiCall<DeleteLeadResponse>(async () => {
        const response = await api.delete<DeleteLeadResponse>({
          url: getApiUrl(endpoint.leads.permanentlyDeleteLead(leadId)),
          auth: true, // Add authentication
        });
        return response;
      });

      showToast("Lead permanently deleted successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Permanently delete all soft-deleted leads, or only `ids` (the import prompt's matches)
  permanentlyDeleteAllLeads: async (ids?: string[]) => {
    try {
      const result = await handleApiCall<{ message: string; deletedCount: number; totalFound: number; errors?: string[] }>(async () => {
        const response = await api.delete<{ message: string; deletedCount: number; totalFound: number; errors?: string[] }>({
          url: getApiUrl(endpoint.leads.permanentlyDeleteAllLeads),
          auth: true,
          ...(ids && { body: { ids } }),
        });
        return response;
      });

      const deletedCount = (result as any)?.deletedCount || 0;
      showToast(`Permanently deleted ${deletedCount} lead(s)`, "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Leads in Recently Deleted that are the same people as these import rows (same name + phone or email)
  findRecentlyDeletedMatches: async (rows: { name?: string; phone?: string; email?: string }[]) => {
    const result = await handleApiCall<{ ids: string[]; rows: number }>(() =>
      api.post({ url: getApiUrl(endpoint.leads.recentlyDeletedMatches), body: { rows }, auth: true }),
    );
    return { ids: result?.ids ?? [], rows: result?.rows ?? 0 };
  },

  uploadLead: async (uploadleadData: FormData) => {
    try {
      // Extract file from FormData
      const file = uploadleadData.get("file") as File;
      if (!file) {
        throw new Error("No file provided for upload");
      }

      // Extract import name from FormData (should be truncated to 255 chars)
      const importName = uploadleadData.get("importName") as string;
      if (!importName) {
        throw new Error("No import name provided");
      }

      // Import hybrid upload service
      const { hybridUploadService } = await import(
        "@/app/_module/services/hybridUploadService"
      );

      // Upload file to Supabase using hybrid upload
      const uploadResult = await hybridUploadService.hybridUpload(
        file,
        "lead-imports",
        undefined, // onProgress callback
        undefined // onStatusUpdate callback
      );

      // Create lead import with file URL
      const importData = {
        fileUrl: uploadResult.downloadUrl,
        filePath: uploadResult.filePath,
        fileName: file.name,
        importName: importName, // Add import name from FormData
        fileSize: uploadResult.fileSize,
        mimeType: uploadResult.mimeType,
      };

      return await handleApiCall<TFileUploadResponse>(async () => {
        const response = await api.post<TFileUploadResponse>({
          url: getApiUrl(endpoint.leads.uploadLeads),
          body: importData,
          auth: true,
          options: {
            headers: {
              "Content-Type": "application/json",
            },
          },
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  getLeadsImportList: async () => {
    try {
      return await handleApiCall<TLeadsImportListResponse>(async () => {
        const response = await api.get<TLeadsImportListResponse>({
          url: getApiUrl(endpoint.leads.getLeadsImportList),
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  getLeadsImportById: async (
    importId: string,
    param?: TGetLeadsImportbyIdParams
  ) => {
    let params;
    if (param) {
      params = getParams<TGetLeadsImportbyIdParams>(param);
    }

    const url = params
      ? constructUrl(
          getApiUrl(endpoint.leads.getLeadsImportById(importId)),
          params
        )
      : getApiUrl(endpoint.leads.getLeadsImportById(importId));

    try {
      return await handleApiCall<TGetLeadsImportbyIdResponse>(async () => {
        const response = await api.get<TGetLeadsImportbyIdResponse>({
          url,
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  getStatusesSimple: async (): Promise<{ value: string; label: string }[]> => {
    try {
      const result = await handleApiCall<{
        statuses: { value: string; label: string }[];
      }>(async () => {
        const response = await api.get<{
          statuses: { value: string; label: string }[];
        }>({
          url: getApiUrl(endpoint.leads.getStatusesSimple),
          auth: true, // Add authentication
        });
        return response;
      });
      return result.statuses || [];
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  updateLead: async (
    leadId: string,
    data: Partial<{
      name?: string;
      assigned?: string;
      leadStat?: string;
      marital?: string;
      license?: string;
      employmentStatus?: string;
      earned?: string;
      height?: string;
      weight?: string;
      unearned?: string;
      worth?: string;
      birthday?: string;
      email?: string;
      phone?: string;
      lastCall?: string;
      longestCall?: string;
      lastCallAt?: string;
      lastLongestCallAt?: string;
      dob?: string;
      gender?: string;
      listName?: string | null;
      noSaleReason?: string;
    }>
  ) => {
    try {
      return await handleApiCall(async () => {
        const response = await api.patch({
          url: getApiUrl(endpoint.leads.updateLead(leadId)),
          body: data,
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      // showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  activateLeadsImport: async (
    importId: string,
    param?: TActivateLeadsImportParams
  ) => {
    let params;
    if (param) {
      params = getParams<TActivateLeadsImportParams>(param);
    }

    const url = params
      ? constructUrl(
          getApiUrl(endpoint.leads.activateLeadsImportById(importId)),
          params
        )
      : getApiUrl(endpoint.leads.activateLeadsImportById(importId));

    try {
      return await handleApiCall<TActivateLeadsImportResponseData>(async () => {
        const response = await api.post<TActivateLeadsImportResponseData>({
          url,
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  activateSingleLeadFromImport: async (importId: string, resultId: string) => {
    try {
      return await handleApiCall<TActivateSingleImportResponseData>(
        async () => {
          const response = await api.post<TActivateSingleImportResponseData>({
            url: getApiUrl(
              endpoint.leads.activateSingleLeadFromImport(importId, resultId)
            ),
            auth: true, // Add authentication
          });
          return response;
        }
      );
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  getImportDeletionPreview: async (importId: string) => {
    try {
      // handleApiCall unwraps the standardized { data } envelope.
      return await handleApiCall<TImportDeletionPreview>(async () => {
        const response = await api.get<TImportDeletionPreview>({
          url: getApiUrl(endpoint.leads.getImportDeletionPreview(importId)),
          auth: true,
        });
        return response;
      });
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  deleteImport: async (importId: string) => {
    try {
      return await handleApiCall<TImportDeletionResult>(async () => {
        const response = await api.delete<TImportDeletionResult>({
          url: getApiUrl(endpoint.leads.deleteImport(importId)),
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  getDeletedImports: async () => {
    try {
      return await handleApiCall<TLeadsImportListResponse>(async () => {
        const response = await api.get<TLeadsImportListResponse>({
          url: getApiUrl(endpoint.leads.getDeletedImports),
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  restoreImport: async (importId: string) => {
    try {
      return await handleApiCall<{ fileName: string | null; restoredLeads: number; restoredHouseholds: number; alreadyActive: boolean }>(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.restoreImport(importId)),
          body: {},
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  bulkDeleteImports: async (importIds: string[]) => {
    try {
      return await handleApiCall<{ jobId: string; status: string; totalLists: number }>(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.bulkDeleteImports),
          body: { importIds },
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  getBulkDeleteJobs: async () => {
    return await handleApiCall<any[]>(async () => {
      const response = await api.get({
        url: getApiUrl(endpoint.leads.getBulkDeleteJobs),
        auth: true,
      });
      return response;
    });
  },

  moveImportToStage: async (importId: string, newStatus: string) => {
    try {
      return await handleApiCall<TImportMoveResult>(async () => {
        const response = await api.post<TImportMoveResult>({
          url: getApiUrl(endpoint.leads.moveImportToStage(importId)),
          body: { newStatus },
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  getStatusesAvailable: async (): Promise<TAvailableStatusesData> => {
    try {
      // Fetch from backend instead of hardcoded values
      return await handleApiCall<TAvailableStatusesData>(async () => {
        const response = await api.get<TAvailableStatusesData>({
          url: getApiUrl(endpoint.leads.getStatusesAvailable),
          auth: true,
        });
        return response;
      });
    } catch (error) {
      // Fallback to hardcoded values if backend fails

      return {
        statuses: [
          { value: "Underwriting", label: "Underwriting" },
          { value: "New lead", label: "New lead" },
          { value: "Appt", label: "Appt" },
          { value: "No sale", label: "No sale" },
          { value: "Sold & Approved", label: "Sold & Approved" },
          { value: "Bad number", label: "Bad number" },
          { value: "Do not call", label: "Do not call" },
          { value: "Disposed", label: "Disposed" },
        ],
        totalCount: 8,
        timestamp: new Date().toISOString(),
      };
    }
  },

  // Create policy for a lead
  createPolicy: async (params: CreatePolicyParams) => {
    try {
      const result = await handleApiCall<CreatePolicyResponse>(async () => {
        const response = await api.post<CreatePolicyResponse>({
          url: getApiUrl(endpoint.policy.createPolicy),
          body: params,
          auth: true, // Add authentication
        });
        return response;
      });

      showToast("Policy created successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Get lead with policies
 getLeadWithPolicies: async (leadId: string) => {
  try {
    const result = await handleApiCall(async () => {
      const response = await api.get<LeadWithPoliciesResponse>({
        url: getApiUrl(endpoint.leads.getLeadWithPolicy(leadId)),
        auth: true,
      });
      return response;
    });
    const leadData = 
      (result as any)?.data?.data || 
      (result as any)?.data ||    
      result;                        
    
    return leadData;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
},


  getDeletedLeads: async (q?: string, page: number = 1, limit: number = 50) => {
    try {
      return await handleApiCall<GetDeletedLeadsResponse>(async () => {
        const baseUrl = getApiUrl(endpoint.leads.getDeletedLeads);
        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('limit', String(limit));
        if (q && q.trim()) params.set('q', q.trim());
        const url = `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params.toString()}`;
        const response = await api.get<GetDeletedLeadsResponse>({
          url,
          auth: true, // Add authentication
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Restore all soft-deleted leads
  restoreAllDeletedLeads: async () => {
    try {
      const result = await handleApiCall<{ message: string; restored: number; total: number }>(async () => {
        const response = await api.post<{ message: string; restored: number; total: number }>({
          url: getApiUrl(endpoint.leads.restoreAllDeletedLeads),
          auth: true,
        });
        return response;
      });

      const restoredCount = (result as any)?.restored || 0;
      showToast(`Successfully restored ${restoredCount} lead(s)`, "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Create lead note
  createLeadNote: async (leadId: string, params: CreateLeadNoteRequest) => {
    try {
      const result = await handleApiCall<CreateLeadNoteResponse>(async () => {
        const response = await api.post<CreateLeadNoteResponse>({
          url: getApiUrl(endpoint.leads.createLeadNote(leadId)),
          body: params,
          auth: true, // Add authentication
        });
        return response;
      });

      showToast("Lead note created successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Delete lead note
  deleteLeadNote: async (leadId: string, noteId: string) => {
    try {
      const result = await handleApiCall<DeleteLeadNoteResponse>(async () => {
        const response = await api.delete<DeleteLeadNoteResponse>({
          url: getApiUrl(endpoint.leads.deleteLeadNote(leadId, noteId)),
          auth: true, // Add authentication
        });
        return response;
      });

      showToast("Lead note deleted successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

// Update policy
updatePolicy: async (policyId: string, data: any) => {
  try {
    const result = await handleApiCall(async () => {
      const response = await api.put({ // ✅ Changed from api.patch to api.put
        url: getApiUrl(endpoint.policy.updatePolicy(policyId)), // ✅ Changed from endpoint.leads.updatePolicy
        body: data,
        auth: true,
      });
      return response;
    });

    showToast("Policy updated successfully", "success");
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
},
  // Household Management (migrated from beneficiaries)
  
  // Get all household members for a lead
  getLeadHouseholds: async (leadId: string) => {
    try {
      return await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leads.getLeadHouseholds(leadId)),
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Create a new household with members
  createHousehold: async (params: { 
    primaryLeadId: string; 
    members: Array<{ name: string }> 
  }) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.createHousehold),
          body: params,
          auth: true,
        });
        return response;
      });

      showToast("Household member added successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Add a member to an existing household
  addHouseholdMember: async (householdId: string, memberData: { name: string }) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.addHouseholdMember(householdId)),
          body: memberData,
          auth: true,
        });
        return response;
      });

      showToast("Household member added successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Remove a member from household
  removeHouseholdMember: async (leadId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.delete({
          url: getApiUrl(endpoint.leads.removeHouseholdMember(leadId)),
          auth: true,
        });
        return response;
      });

      showToast("Household member removed successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Disband a household entirely — every member becomes a standalone lead.
  // Grouping is automatic, so this is the agent's undo for a wrong group; removing
  // members one at a time is unusable past a handful.
  disbandHousehold: async (householdId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.disbandHousehold(householdId)),
          data: {},
          auth: true,
        });
        return response;
      });

      const count = (result as any)?.data?.disbandedCount;
      showToast(
        count
          ? `Household disbanded — ${count} lead${count === 1 ? "" : "s"} now standalone`
          : "Household disbanded",
        "success",
      );
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Put a lead the agent picked in this lead's household (backend: POST /households/link).
  // Grouping only joins on a shared phone or address; this is the agent's way to join the rest.
  linkHouseholdLead: async (leadId: string, memberLeadId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.linkHouseholdLead),
          body: { leadId, memberLeadId },
          auth: true,
        });
        return response;
      });
      showToast("Added to household", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Bring a household member the agent can't open into their own leads
  // (backend: POST /leads/:leadId/household-restore). Only the agent holding the
  // house lead can, and only for a member nobody holds yet.
  restoreHouseholdMember: async (leadId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.restoreHouseholdMember(leadId)),
          data: {},
          auth: true,
        });
        return response;
      });
      showToast((result as any)?.message || "Restored to your leads", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Update household member
  updateHouseholdMember: async (leadId: string, data: any) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.patch({
          url: getApiUrl(endpoint.leads.updateLead(leadId)),
          body: data,
          auth: true,
        });
        return response;
      });

      showToast("Household member updated successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Switch primary household member
  switchPrimaryMember: async (householdId: string, newPrimaryLeadId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.put({
          url: getApiUrl(endpoint.leads.switchPrimaryMember(householdId)),
          body: { newPrimaryLeadId },
          auth: true,
        });
        return response;
      });

      showToast("Primary household member switched successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },

  // Get household details
  getHousehold: async (householdId: string) => {
    try {
      return await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leads.getHousehold(householdId)),
          auth: true,
        });
        return response;
      });
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw transformErrorResponse(error);
    }
  },


  bulkImportLeads: async (params: {
    file: File;
    importName: string;
    createImmediately?: boolean;
    defaultStatus?: string;
    listName?: string;
  }) => {
    try {
      const { file, importName, createImmediately = true, defaultStatus, listName } = params;

      // Create FormData for multipart/form-data upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('importName', importName);
      formData.append('createImmediately', String(createImmediately));
      if (defaultStatus) {
        formData.append('defaultStatus', defaultStatus);
      }
      if (listName) {
        formData.append('listName', listName);
      }

      // Use the bulk import endpoint - endpoint.leads.uploadLeads is "/leads/import"
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.leads.uploadLeads),
          body: formData,
          auth: true,
        });
        return response;
      });

      // Extract import job data from response
      const importJob = (result as any)?.data?.data || (result as any)?.data || result;
      
      return {
        success: true,
        importId: importJob.id,
        importJob,
      };

    } catch (error: any) {
      // CSV imports are rate-limited (LeadImportThrottlerGuard → HTTP 429). The
      // global axios interceptor already shows the cool-down toast; here we just
      // re-throw a normalized 429 so the import-progress box (MapImport) renders
      // its amber "cooling down" state instead of a red "failed".
      if (isRateLimitError(error)) {
        throw {
          ...(error && typeof error === 'object' ? error : {}),
          statusCode: 429,
          code: '429',
          isRateLimit: true,
          message: getRateLimitMessage(error),
        };
      }

      showToast(
        transformErrorResponse(error)?.message || 'Failed to start import',
        'error'
      );
      throw transformErrorResponse(error);
    }
  },

  getImportStatus: async (importId: string) => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.leads.getLeadsImportById(importId)),
          auth: true,
        });
        return response;
      });

      // Extract job data from response
      const jobData = (result as any)?.data?.data || (result as any)?.data || result;
      
      return jobData;
    } catch (error) {
      throw transformErrorResponse(error);
    }
  },

  // ============================================================
  // Lead List Names (Sources)
  // ============================================================

  getListNames: async (): Promise<string[]> => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.leads.getListNames),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  getTags: async (): Promise<string[]> => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.leads.getTags),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  getCarriers: async (): Promise<string[]> => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl('/leads/carriers'),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  getCities: async (): Promise<string[]> => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl('/leads/cities'),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  // Custom-field headers available for the sort: [{ key, type: 'numeric' | 'text' }]
  getCustomFieldKeys: async (): Promise<Array<{ key: string; type: string }>> => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.leads.getCustomFieldKeys),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  // Distinct values of a single custom field, for the value filter. `scope` = the list's own filters, so
  // only the leads on screen count: the Leads page sends them with scope=list; Lead Assignment's go to its
  // own endpoint (`source: "assignment"`).
  getCustomFieldValues: async (key: string, scope?: Record<string, string | undefined>, source: "leads" | "assignment" = "leads"): Promise<string[]> => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(scope ?? {})) if (v != null && v !== "") params.set(k, v);
    params.set("key", key);
    const base = source === "assignment" ? endpoint.leadAssignment.getCustomFieldValues : endpoint.leads.getCustomFieldValues;
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(`${base}?${params.toString()}`),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  // ============================================================
  // Pipeline Stages
  // ============================================================

  getPipelineStages: async () => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.leads.getPipelineStages),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  getAllPipelineStages: async () => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.leads.getAllPipelineStages),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  createPipelineStage: async (data: { name: string; color: string; icon?: string }) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.post<any>({
        url: getApiUrl(endpoint.leads.createPipelineStage),
        body: data,
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result;
  },

  updatePipelineStage: async (stageId: string, data: { name?: string; color?: string; icon?: string }) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.patch<any>({
        url: getApiUrl(endpoint.leads.updatePipelineStage(stageId)),
        body: data,
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result;
  },

  togglePipelineStageVisibility: async (stageId: string) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.patch<any>({
        url: getApiUrl(endpoint.leads.togglePipelineStageVisibility(stageId)),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result;
  },

  deletePipelineStage: async (stageId: string) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.delete<any>({
        url: getApiUrl(endpoint.leads.deletePipelineStage(stageId)),
        auth: true,
      });
      return response;
    });
    return result;
  },

  getDeletedPipelineStages: async () => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.leads.getDeletedPipelineStages),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  getPipelineStageImpact: async (stageId: string) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.get<any>({
        url: getApiUrl(endpoint.leads.pipelineStageImpact(stageId)),
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result;
  },

  restorePipelineStage: async (stageId: string) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.post<any>({
        url: getApiUrl(endpoint.leads.restorePipelineStage(stageId)),
        body: {},
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result;
  },

  reorderPipelineStages: async (stageIds: string[]) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.post<any>({
        url: getApiUrl(endpoint.leads.reorderPipelineStages),
        body: { stageIds },
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  resetPipelineStageOrder: async () => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.post<any>({
        url: getApiUrl(endpoint.leads.resetPipelineStageOrder),
        body: {},
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result ?? [];
  },

  bulkUpdateLeadStatus: async (leadIds: string[], newStatus: string) => {
    const result = await handleApiCall<any>(async () => {
      const response = await api.post<any>({
        url: getApiUrl(endpoint.leads.bulkUpdateLeadStatus),
        body: { leadIds, newStatus },
        auth: true,
      });
      return response;
    });
    return (result as any)?.data ?? result;
  },
};

 export const beneficiaryService = {
  /**
   * Create multiple beneficiaries for a lead
   * @param leadId - The parent lead ID
   * @param beneficiaries - Array of beneficiary data
   */
  createBeneficiaries: async (
    leadId: string,
    beneficiaries: Array<{
      name: string;
      relationship?: string;
      percentage?: number;
      dob?: string;  // Frontend form uses 'dob', backend expects 'dateOfBirth'
      ssn?: string;
      contingentName?: string;
      contingentRelationship?: string;
      contingentPercentage?: number;
    }>
  ) => {
    try {
      // Transform 'dob' to 'dateOfBirth' for backend
      const transformedBeneficiaries = beneficiaries.map(b => ({
        name: b.name,
        relationship: b.relationship,
        percentage: b.percentage,
        dateOfBirth: b.dob, // Map 'dob' to 'dateOfBirth'
        ssn: b.ssn,
        contingentName: b.contingentName,
        contingentRelationship: b.contingentRelationship,
        contingentPercentage: b.contingentPercentage,
      }));

      const payload: CreateBeneficiariesRequest = {
        parentLeadId: leadId,
        beneficiaries: transformedBeneficiaries,
      };

      const result = await handleApiCall<CreateBeneficiariesResponse>(
        async () => {
          const response = await api.post<CreateBeneficiariesResponse>({
            url: getApiUrl(endpoint.leads.createLeadBeneficiaries),
            body: payload,
            auth: true,
          });
          return response;
        }
      );

      showToast("Beneficiaries created successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },

  /**
   * Get all beneficiaries for a lead
   * @param leadId - The parent lead ID
   */
getBeneficiaries: async (leadId: string) => {
  try {
    const result = await handleApiCall<GetBeneficiariesResponse>(async () => {
      const response = await api.get<GetBeneficiariesResponse>({
        url: getApiUrl(endpoint.leads.getLeadBeneficiaries(leadId)),
        auth: true,
      });
      return response;
    });
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
},  /**
   * Update a beneficiary
   * @param leadId - The parent lead ID (required for endpoint)
   * @param beneficiaryId - The beneficiary ID to update
   * @param data - The fields to update
   */
updateBeneficiary: async (
  leadId: string,
  beneficiaryId: string,
  data: UpdateBeneficiaryRequest
) => {
  try {
    // Transform 'dob' to 'dateOfBirth' for backend
    const transformedData: any = { ...data };
    if ('dob' in data) {
      transformedData.dateOfBirth = data.dob;
      delete transformedData.dob;
    }

    const result = await handleApiCall(async () => {
      const response = await api.patch({
        url: getApiUrl(endpoint.leads.updateLeadBeneficiary(leadId, beneficiaryId)),
        body: transformedData,
        auth: true,
      });
      return response;
    });

    showToast("Beneficiary updated successfully", "success");
    return result;
  } catch (error) {
    showToast(transformErrorResponse(error)?.message, "error");
    throw transformErrorResponse(error);
  }
},
  /**
   * Delete a beneficiary
   * @param leadId - The parent lead ID (required for endpoint)
   * @param beneficiaryId - The beneficiary ID to delete
   */
  deleteBeneficiary: async (leadId: string, beneficiaryId: string) => {
    try {
      const result = await handleApiCall<DeleteBeneficiaryResponse>(
        async () => {
          const response = await api.delete<DeleteBeneficiaryResponse>({
            url: getApiUrl(endpoint.leads.deleteLeadBeneficiary(leadId, beneficiaryId)),
            auth: true,
          });
          return response;
        }
      );

      showToast("Beneficiary deleted successfully", "success");
      return result;
    } catch (error) {
      showToast(transformErrorResponse(error)?.message, "error");
      throw transformErrorResponse(error);
    }
  },
 }